import { create } from 'zustand';

export type SimpleAiJobStatus = 'pending' | 'processing' | 'completed' | 'failed';

export type SimpleAiJob = {
  id: string;
  flowId: string;
  flowTitle: string;
  status: SimpleAiJobStatus;
  progress: number;
  structureReady: boolean;
  completedChapters: number;
  totalChapters: number;
  currentChapterLabel: string | null;
  errorMessage: string | null;
  createdAt: string;
  updatedAt: string;
};

interface SimpleAiJobState {
  activeJobs: SimpleAiJob[];
  bannerHiddenJobIds: string[];
  setActiveJobs: (jobs: SimpleAiJob[]) => void;
  upsertJob: (job: SimpleAiJob) => void;
  removeJob: (jobId: string) => void;
  hideBanner: (jobId: string) => void;
}

export const useSimpleAiJobStore = create<SimpleAiJobState>((set) => ({
  activeJobs: [],
  bannerHiddenJobIds: [],
  setActiveJobs: (jobs) =>
    set((state) => ({
      activeJobs: jobs,
      bannerHiddenJobIds: state.bannerHiddenJobIds.filter((id) => jobs.some((j) => j.id === id)),
    })),
  upsertJob: (job) =>
    set((state) => {
      const others = state.activeJobs.filter((j) => j.id !== job.id);
      if (job.status === 'pending' || job.status === 'processing') {
        return { activeJobs: [job, ...others] };
      }
      return {
        activeJobs: others,
        bannerHiddenJobIds: state.bannerHiddenJobIds.filter((id) => id !== job.id),
      };
    }),
  removeJob: (jobId) =>
    set((state) => ({
      activeJobs: state.activeJobs.filter((j) => j.id !== jobId),
      bannerHiddenJobIds: state.bannerHiddenJobIds.filter((id) => id !== jobId),
    })),
  hideBanner: (jobId) =>
    set((state) => ({
      bannerHiddenJobIds: state.bannerHiddenJobIds.includes(jobId)
        ? state.bannerHiddenJobIds
        : [...state.bannerHiddenJobIds, jobId],
    })),
}));

export function isSimpleAiJobActive(job: SimpleAiJob): boolean {
  return job.status === 'pending' || job.status === 'processing';
}
